import libUrl from 'pdfjs-dist/build/pdf.min.mjs?url';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/**
 * What the page and the document frame (frame.ts) say to each other, and the
 * pdf.js sources the page hands the frame.
 */

/** A step of the zoom, or one of the two fits that are its ends: the whole page (out) and the width (in). */
export type ZoomMode = 'in' | 'out' | 'width' | 'page';

/**
 * The page to the frame. `token` names an open: the page gives each document
 * its own, and the frame says it back in every word about that document, so
 * a word about the one before (said before the next open arrived) is known
 * for what it is.
 */
export type ToFrame =
  | { type: 'open'; token: number; pdf: ArrayBuffer; lib: string; worker: string; bg?: string; fg?: string }
  | { type: 'theme'; bg?: string; fg?: string }
  | { type: 'goto'; page: number }
  | { type: 'zoom'; mode: ZoomMode }
  /** A share of the way from the whole page (0) to the width (1), evenly on a log scale: the digit ladder. */
  | { type: 'zoom'; mode: 'between'; share: number };

/** The frame to the page. */
export type FromFrame =
  | { type: 'hello' }
  | { type: 'loaded'; token: number; pages: number }
  | { type: 'ready'; token: number }
  | { type: 'page'; token: number; page: number }
  /** Where the zoom is — at a fit, or not — as a percentage of the width fit, and as far in as it goes (`max`), the same way. */
  | { type: 'zoom'; token: number; fit: 'width' | 'page' | null; percent: number; max: number }
  | { type: 'error'; token: number; message: string }
  | { type: 'log'; message: string }
  | { type: 'key'; key: string; code: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean; repeat: boolean };

const FROM_FRAME = new Set(['hello', 'loaded', 'ready', 'page', 'zoom', 'error', 'log', 'key']);

/** Whether a message is one the frame sends; anything else on the window is not ours. */
export function isFromFrame(data: unknown): data is FromFrame {
  return typeof data === 'object' && data !== null && FROM_FRAME.has((data as { type?: unknown }).type as string);
}

export interface PdfJsSources {
  lib: string;
  worker: string;
}

let sources: Promise<PdfJsSources> | null = null;

/**
 * pdf.js, as text: its two files are shipped untouched (Vite `?url`), fetched
 * once by the page from its own origin and handed to the frame, which loads
 * them from URLs of its own (see frame.ts). Fetched once per session; a
 * failure is not kept, so the next document tries again.
 */
export function pdfJsSources(fetchImpl: typeof fetch = fetch): Promise<PdfJsSources> {
  sources ??= Promise.all([fetchImpl(libUrl), fetchImpl(workerUrl)])
    .then(async ([lib, worker]) => {
      if (!lib.ok || !worker.ok) throw new Error('The document reader could not be loaded.');
      return { lib: await lib.text(), worker: await worker.text() };
    })
    .catch((err: unknown) => {
      sources = null;
      throw err;
    });
  return sources;
}

/** Tests: forget the fetched sources. */
export function resetPdfJsSources(): void {
  sources = null;
}
